import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, Link, useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import defaultAvatar from '../../images/pngs/seller.png';
import Loader from '../../Components/utils/Loader';
import axios from 'axios';
import ReviewPopup from '../../Components/buyer/ReviewPopup';
import ReviewHistoryContainer from '../../Components/buyer/ReviewHistoryContainer';
import OrderChatInput from '../../Components/buyer/OrderChatInput';
import StartDisputeModal from '../../Components/buyer/StartDisputeModal';
import DisputeSection from '../../Components/buyer/DisputeSection';
import { LoginContext } from '../../Contexts/LoginContext';
import { FloatingChatContext } from '../../Contexts/FloatingChatContext';
import { FiInfo } from 'react-icons/fi';
import { FaChevronDown, FaChevronUp, FaSyncAlt } from 'react-icons/fa';
import { FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileAlt } from 'react-icons/fa';
import { HiDocumentText } from 'react-icons/hi';
import PaymentOverlay from '../../Components/utils/PaymentOverlay';
import { formatCurrency, formatCurrencyDual, formatDateTime, orderSellerFeePercent } from '../../data/constants';
import { formatFullAddress } from '../../Components/utils/formatAddress';
import { showToast } from '../../Components/utils/ShowToast';
import { generateInvoice, generatePackingSlip } from '../../utils/documentGenerator';
import Logo from '../../images/pngs/Logo.png';
import PayoutFeeNote from '../../Components/utils/PayoutFeeNote';

const API = process.env.REACT_APP_Backend_Host_Name;

const renderDocumentIcon = (fileType) => {
  if (fileType?.startsWith('application/pdf')) return <FaFilePdf size={16} />;
  if (fileType?.includes('word')) return <FaFileWord size={16} />;
  if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return <FaFileExcel size={16} />;
  if (fileType?.includes('powerpoint') || fileType?.includes('presentation')) return <FaFilePowerpoint size={16} />;
  return <FaFileAlt size={16} />;
};

const ORDER_CHAT_COLLAPSED_KEY = 'orderChatCollapsed';

const OrderDetailsPage = () => {
  const location = useLocation();
  const { orderId } = useParams();
  const isSeller = location.pathname.startsWith('/sellerOrderDetailPage');
  const { openFloatingChat } = useContext(FloatingChatContext);
  const { user, feesObj, currency, exchangeRate } = useContext(LoginContext);
  const [orderData, setOrderData] = useState(null);
  const [orderChatMessages, setOrderChatMessages] = useState([]);
  const [collapsedChatIds, setCollapsedChatIds] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const [modal, setModal] = useState({ open: false, type: 'message', message: '', onConfirm: null });
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewPostedPopup, setShowReviewPostedPopup] = useState(false);
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [showStartDisputeModal, setShowStartDisputeModal] = useState(false);
  const socketRef = useRef(null);

  const refreshOrder = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(response.data.order);
    } catch (err) {
      console.error('Error refreshing order:', err);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      await generateInvoice(orderData, { currency, exchangeRate, logoPath: Logo, isSeller, feesObj });
      showToast('Invoice generated', 'success');
    } catch (err) {
      showToast('Failed to generate invoice', 'error');
    }
  };

  const handleGeneratePackingSlip = async () => {
    try {
      await generatePackingSlip(orderData, { currency, exchangeRate, logoPath: Logo });
      showToast('Packing slip generated', 'success');
    } catch (err) {
      showToast('Failed to generate packing slip', 'error');
    }
  };

  const navigate = useNavigate();

  // Redirect to correct order-detail URL by role: seller must use sellerOrderDetailPage, buyer must use orderDetailPage
  useEffect(() => {
    if (!user || !orderId) return;
    const isSellerPath = location.pathname.startsWith('/sellerOrderDetailPage');
    const userIsSeller = user?.role === 'seller';
    if (userIsSeller && !isSellerPath) {
      navigate(`/sellerOrderDetailPage/${orderId}`, { replace: true });
      return;
    }
    if (!userIsSeller && isSellerPath) {
      navigate(`/orderDetailPage/${orderId}`, { replace: true });
      return;
    }
  }, [user, orderId, location.pathname, navigate]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrderData(response.data.order);
      } catch (error) {
        console.error('Error fetching order:', error);
        setOrderData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  useEffect(() => {
    if (!orderId) return;
    try {
      const stored = localStorage.getItem(`${ORDER_CHAT_COLLAPSED_KEY}_${orderId}`);
      setCollapsedChatIds(stored ? JSON.parse(stored) : {});
    } catch { setCollapsedChatIds({}); }
  }, [orderId]);

  useEffect(() => {
    if (!orderId || !user) return;
    const token = localStorage.getItem('token');
    const fetchChat = async () => {
      try {
        const res = await axios.get(`${API}/api/v1/orders/${orderId}/chat`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) setOrderChatMessages(res.data.messages || []);
      } catch { }
    };
    fetchChat();
  }, [orderId, user]);

  useEffect(() => {
    if (!user) return;
    socketRef.current = io(API, { auth: { token: localStorage.getItem('token') } });
    return () => { if (socketRef.current) socketRef.current.disconnect(); };
  }, [user]);

  useEffect(() => {
    if (!socketRef.current || !orderId) return;
    const handler = ({ orderId: msgOrderId, message: newMsg }) => {
      if (msgOrderId === orderId) setOrderChatMessages(prev => [...prev, newMsg]);
    };
    socketRef.current.on('orderChatMessage', handler);
    return () => socketRef.current?.off('orderChatMessage', handler);
  }, [orderId]);

  const toggleChatExpanded = (msgId) => {
    setCollapsedChatIds(prev => {
      const next = { ...prev, [msgId]: !prev[msgId] };
      try {
        localStorage.setItem(`${ORDER_CHAT_COLLAPSED_KEY}_${orderId}`, JSON.stringify(next));
      } catch { }
      return next;
    });
  };

  const handleFileDownload = async (fileUrl, originalFileName) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios({
        url: `${API}/api/v1/chats/download`,
        method: 'GET',
        params: { fileUrl, originalFileName },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalFileName || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const status = err?.response?.status;
      if (!status || status >= 400) showToast('Download failed', 'error');
    }
  };

  useEffect(() => {
    if (!orderData) return;

    const shouldShowCountdown =
      (isSeller && orderData.deliveryType === 'delivery' && !['Delivered', 'Cancelled', 'Completed', 'Resolved', 'OnHold', 'InDispute'].includes(orderData.currentStatus)) ||
      (!isSeller && orderData.deliveryType === 'pickup' && !['Picked Up', 'Cancelled', 'Completed', 'Resolved', 'OnHold', 'InDispute'].includes(orderData.currentStatus));

    if (!shouldShowCountdown) {
      setTimeLeft(null);
      return;
    }

    const durationStr = isSeller ? orderData.timeToDeliver : orderData.timeframeToCollect;
    const durationMs = parseDuration(durationStr);
    if (!durationMs || durationMs <= 0) {
      setTimeLeft(null);
      return;
    }

    const createdAt = new Date(orderData.createdAt);
    const deadline = new Date(createdAt.getTime() + durationMs);

    const updateTimeLeft = () => {
      const now = new Date();
      const timeLeftMs = deadline - now;
      setTimeLeft(timeLeftMs > 0 ? timeLeftMs : 0);
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [orderData, isSeller]);

  useEffect(() => {
    if (localStorage.getItem("Review Submitted"))
      setShowReviewPostedPopup(true);
  }, []);

  const parseDuration = (str) => {
    if (!str || typeof str !== 'string') return 0;
    const match = str.match(/^(\d+)\s*(hour|hours|day|days|minute|minutes)$/i);
    if (!match) return 0;
    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    switch (unit) {
      case 'hour':
      case 'hours':
        return value * 60 * 60 * 1000;
      case 'day':
      case 'days':
        return value * 24 * 60 * 60 * 1000;
      case 'minute':
      case 'minutes':
        return value * 60 * 1000;
      default:
        return 0;
    }
  };

  const formatTimeLeft = (ms) => {
    if (ms <= 0) return 'Deadline passed';
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    ms %= 24 * 60 * 60 * 1000;
    const hours = Math.floor(ms / (60 * 60 * 1000));
    ms %= 60 * 60 * 1000;
    const minutes = Math.floor(ms / (60 * 1000));
    return `${days}d ${hours}h ${minutes}m`;
  };

  const handleAccept = async () => {
    if (orderData.orderType === 'deposit' && orderData.payment.remainingPayment.status !== 'succeeded') {
      setModal({
        open: true,
        type: 'message',
        message: 'You cannot accept the order until you pay the remaining balance.',
        onConfirm: null,
      });
      return;
    }
    setIsActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/accept-order`,
        { orderId: orderData._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModal({
        open: true,
        type: 'message',
        message: 'Order accepted successfully.',
        onConfirm: null,
      });
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(response.data.order);
    } catch (error) {
      console.error('Error accepting order:', error);
      setModal({
        open: true,
        type: 'message',
        message: 'An error occurred while accepting the order',
        onConfirm: null,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleReject = async () => {
    setIsActionLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/reject-order`,
        { orderId: orderData._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModal({
        open: true,
        type: 'message',
        message: 'Order rejected successfully. Please refresh the page to see the updated status.',
        onConfirm: null,
      });
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(response.data.order);
    } catch (error) {
      console.error('Error rejecting order:', error);
      setModal({
        open: true,
        type: 'message',
        message: 'An error occurred while rejecting the order',
        onConfirm: null,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCancelOrder = () => {
    setCancelModalOpen(true);
  };

  const submitCancelRequest = async () => {
    if (!cancelReason.trim()) {
      setModal({
        open: true,
        type: 'message',
        message: 'Please provide a reason for cancellation',
        onConfirm: null,
      });
      return;
    }
    setIsActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/request-cancellation`,
        { orderId: orderData._id, reason: cancelReason, requestedBy: isSeller ? 'seller' : 'buyer' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModal({
        open: true,
        type: 'message',
        message: 'Cancellation request submitted successfully.',
        onConfirm: null,
      });
      const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderData(response.data.order);
      setCancelModalOpen(false);
      setCancelReason('');
    } catch (error) {
      console.error('Error requesting cancellation:', error);
      setModal({
        open: true,
        type: 'message',
        message: 'An error occurred while requesting cancellation',
        onConfirm: null,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleAcceptCancellation = () => {
    const confirmMessage = isNonRefundable
      ? 'This is a non-refundable deposit order. Accepting will cancel the order and the buyer will not receive any refund. Are you sure you want to accept the cancellation request?'
      : 'Are you sure you want to accept the cancellation request? The refund amount will be added to the buyer\'s Chocowi account for future use or payout.';
    setModal({
      open: true,
      type: 'confirm',
      message: confirmMessage,
      onConfirm: async () => {
        setIsActionLoading(true);
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/accept-cancellation`,
            { orderId: orderData._id, acceptedBy: isSeller ? 'seller' : 'buyer' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setModal({
            open: true,
            type: 'message',
            message: 'Cancellation request accepted.',
            onConfirm: null,
          });
          const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrderData(response.data.order);
        } catch (error) {
          console.error('Error accepting cancellation:', error);
          setModal({
            open: true,
            type: 'message',
            message: 'An error occurred while accepting the cancellation',
            onConfirm: null,
          });
        } finally {
          setIsActionLoading(false);
        }
      },
    });
  };

  const handleRejectCancellation = () => {
    setModal({
      open: true,
      type: 'confirm',
      message: 'Are you sure you want to reject the cancellation request?',
      onConfirm: async () => {
        setIsActionLoading(true);
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/reject-cancellation`,
            { orderId: orderData._id, rejectedBy: isSeller ? 'seller' : 'buyer' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setModal({
            open: true,
            type: 'message',
            message: 'Cancellation request rejected.',
            onConfirm: null,
          });
          const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setOrderData(response.data.order);
        } catch (error) {
          console.error('Error rejecting cancellation:', error);
          setModal({
            open: true,
            type: 'message',
            message: 'An error occurred while rejecting the cancellation',
            onConfirm: null,
          });
        } finally {
          setIsActionLoading(false);
        }
      },
    });
  };

  const handleSubmitReview = async (reviewData) => {
    try {
      const formData = new FormData();
      formData.append('orderId', selectedReview.customOrderId);
      formData.append('ratings', JSON.stringify(reviewData.ratings));
      formData.append('comment', reviewData.comment);
      reviewData.attachedImages.forEach((image, index) => {
        formData.append('images', image);
      });

      const response = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/reviews`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newReview = response.data.review;
      setOrderData(prev => ({
        ...prev,
        reviewed: true,
        review: newReview
      }));
      setShowReviewPopup(false);
      localStorage.setItem("Review Submitted", true);
      window.location.reload();
    } catch (error) {
      console.error('Error submitting review', error);
      throw error; // Let ReviewPopup handle the error
    }
  };

  if (isLoading) {
    return (
      <div className="orderDetailLoader">
        <Loader type="simple" />
      </div>
    );
  }

  if (!orderData) {
    return <div>Order not found</div>;
  }

  const role = user?.role === 'seller' ? 'seller' : 'buyer';
  const lastStatus = orderData.statusHistory[0];
  const isNonRefundable = orderData?.orderType === 'deposit' && orderData?.refundPolicy === 'non_refundable';

  const otherPartyInfo = isSeller
    ? {
      name: orderData?.buyerInfo?.fullName || 'N/A',
      email: orderData?.buyerInfo?.email || 'N/A',
      phone: orderData?.buyerInfo?.phone || 'N/A',
      avatar: null,
    }
    : {
      name: orderData?.sellerId?.displayName || orderData?.sellerId?.companyName || 'N/A',
      avatar: orderData?.sellerId?.profileImage || defaultAvatar,
    };

  const getSelectedOptionsDescription = (order) => {

    const b2bVariations = order?.productInfo?.selectedOptions?.b2bVariations || [];
    if (b2bVariations.length > 0) {
      return b2bVariations.map((v) => `${v.name}: ${v.selected}`).join(', ');
    }

    if (order.productInfo.pricingType === 'custom' || order.orderType === 'deposit')
      return "[Custom Order]"

    const { pricingType, selectedOptions } = order.productInfo || {};
    if (!pricingType || !selectedOptions) return 'No options selected';
    if (pricingType === 'fixed') {
      return `Package: ${selectedOptions.fixedPricing?.packageName || 'N/A'}`;
    } else if (pricingType === 'variable') {
      const basePrice = selectedOptions.variablePricing?.basePrice || 0;
      const options = selectedOptions.variablePricing?.variables
        ?.map((v) => `${v.name}: ${v.selectedOption?.name} (+$${v.selectedOption?.price || 0})`)
        .join(', ') || 'None';
      return `Base: $${basePrice}, ${options}`;
    }
    return 'No options selected';
  };

  const getStatusStyle = (status) => {
    const statusStyles = {
      'Active': 'ordersStatus ordersStatusActive',
      'Out for Delivery': 'ordersStatus ordersStatusActive',
      'Ready for Pickup': 'ordersStatus ordersStatusActive',
      'Delivered': 'ordersStatus ordersStatusActive',
      'Picked Up': 'ordersStatus ordersStatusActive',
      'Past Due': 'ordersStatus ordersStatusPastDue',
      'InDispute': 'ordersStatus ordersStatusNeedResolution',
      'OnHold': 'ordersStatus ordersStatusNeedResolution',
      'Completed': 'ordersStatus ordersStatusCompleted',
      'Resolved': 'ordersStatus ordersStatusCompleted',
      'Cancelled': 'ordersStatus ordersStatusCancelled',
    };
    return statusStyles[status] || 'ordersStatus';
  };

  const calculateSellerEarnings = (summary) => {
    const subtotal = summary?.subtotal || 0;
    const deliveryFee = summary?.deliveryFee || 0;
    const serviceFee = (orderSellerFeePercent(summary, feesObj.seller) / 100) * (subtotal + deliveryFee);
    const total = subtotal + deliveryFee - serviceFee;
    return {
      subtotal: subtotal.toFixed(2),
      deliveryFee: deliveryFee.toFixed(2),
      serviceFee: serviceFee.toFixed(2),
      total: total.toFixed(2),
    };
  };

  const billingDetails = isSeller
    ? calculateSellerEarnings(orderData?.summary)
    : {
      subtotal: orderData?.summary?.subtotal?.toFixed(2) || '0.00',
      deliveryFee: orderData?.summary?.deliveryFee?.toFixed(2) || '0.00',
      serviceFee: orderData?.summary?.serviceFee?.toFixed(2) || '0.00',
      tax: orderData?.summary?.tax?.toFixed(2) || '0.00',
      total: orderData?.summary?.total?.toFixed(2) || '0.00',
    };

  const canTrackOrder = orderData?._id && orderData?.statusHistory && orderData?.currentStatus && orderData?.timelineStatus && orderData?.customOrderId;

  const reviewData = {
    id: orderData._id,
    customOrderId: orderData.customOrderId,
    image: orderData.productInfo.thumbnail,
    title: orderData.productInfo.title,
    sellerName: orderData.sellerId?.displayName || orderData.sellerId?.companyName || 'Seller',
  };

  return (
    <div className="orderDetailPage">
      <section className="mainSection">
        <div className="orderDetailCard">

          <div className="orderDetailHeader">
            <h1 className="orderDetailTitle">
              {orderData?.productInfo?.listingType === 'service'
                ? 'Service Order'
                : `${orderData.deliveryType?.charAt(0).toUpperCase() + orderData.deliveryType?.slice(1) || 'Unknown'} Order`}
            </h1>
            <div className="orderDetailTrackAndStatus">
              {orderData?.productInfo?.listingType === 'service' && orderData?.productInfo?.productId?.serviceMode === 'online' ? (
                <span className="orderDetailTrackingDisabled">Online Service</span>
              ) : canTrackOrder ? (
                <Link
                  to={isSeller ? `/sellerOrderTrackingPage/${orderData._id}` : `/orderTrackingPage/${orderData._id}`}
                  state={{ delivery: orderData.deliveryType === 'delivery', isSeller }}
                  className={isSeller ? 'orderDetailTracking orderDetailTrackingSeller' : 'orderDetailTracking'}
                >
                  {isSeller ? (
                    <>
                      <FaSyncAlt className="orderDetailTrackingSellerIcon" aria-hidden />
                      Update order status
                    </>
                  ) : (
                    'Track Order'
                  )}
                </Link>
              ) : (
                <span className="orderDetailTrackingDisabled">Track Order (N/A)</span>
              )}
              <span className={getStatusStyle(orderData.currentStatus)}>
                {orderData.currentStatus?.toUpperCase() || 'UNKNOWN'}
              </span>
            </div>
          </div>

          {orderData.dispute && (
            <div className="orderDetailDisputeSectionFull">
              <DisputeSection
                dispute={orderData.dispute}
                orderId={orderData._id}
                orderSummary={orderData.summary}
                isSeller={isSeller}
                currency={currency}
                exchangeRate={exchangeRate}
                onUpdate={refreshOrder}
              />
            </div>
          )}

          <div className="orderDetailContent">
            <div className="orderDetailLeftColumn">
              {!isSeller && (orderData.currentStatus === 'Completed' || orderData.currentStatus === 'Resolved') && !orderData.postedReview && (
                <div className="orderDetailReviewButtonContainer">
                  <button
                    className="modalButton submit"
                    onClick={() => {
                      setSelectedReview(reviewData);
                      setShowReviewPopup(true);
                    }}
                    style={{ width: "100%", marginBottom: "20px" }}
                  >
                    Leave a Review
                  </button>
                </div>
              )}

              {!isSeller && orderData.orderType === 'deposit' && orderData.payment.remainingPayment.status !== 'succeeded' && (
                <div className="orderDetailSection">
                  <h3>Remaining Payment</h3>
                  <p>Remaining Amount: {formatCurrency(orderData.summary.remainingAmount, currency, exchangeRate)}</p>
                  <p>Due Date: {new Date(orderData.remainingBalanceDueDate).toLocaleDateString()}</p>
                  <p>The order will only be completed once you pay the remaining amount.</p>
                  <button className="modalButton submit" onClick={() => setShowPaymentOverlay(true)} style={{ marginTop: "10px" }}>Pay Now</button>
                </div>
              )}

              {!isSeller && (orderData.currentStatus === 'Delivered' || orderData.currentStatus === 'Picked Up') && (
                <div className="orderDetailSection">
                  <h3>Confirm Receipt</h3>
                  <p>
                    {orderData.currentStatus === 'Delivered'
                      ? 'Seller has marked the order as Delivered. Have you received your item?'
                      : 'Seller has marked the order as Picked Up. Have you picked up your item?'}
                  </p>
                  <p style={{ marginTop: '10px', fontSize: '14px', color: '#555', lineHeight: 1.5 }}>
                    If you do not accept or reject within 3 days of delivery or pickup confirmation, the order will be marked as completed automatically.
                    {orderData.buyerConfirmationDueAt && (
                      <span>{' '}You can respond until{' '}
                        <strong>{new Date(orderData.buyerConfirmationDueAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</strong>
                        {' '}(local time).</span>
                    )}
                  </p>
                  <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                    <button className='modalButton submit' onClick={handleAccept} disabled={isActionLoading}>
                      {isActionLoading ? <Loader type="miniPlus" /> : 'Yes'}
                    </button>
                    <button className='modalButton cancel' onClick={handleReject} disabled={isActionLoading}>
                      {isActionLoading ? <Loader type="miniPlus" /> : 'No'}
                    </button>
                  </div>
                </div>
              )}

              {isSeller && (orderData.currentStatus === 'Delivered' || orderData.currentStatus === 'Picked Up') && (
                <div className="orderDetailSection">
                  <h3>Waiting for Approval</h3>
                  <p>
                    {orderData.currentStatus === 'Delivered'
                      ? 'You have marked the order as Delivered. The order will be marked as Completed once the buyer confirms, or automatically after 3 days if the buyer does not respond.'
                      : 'You have marked the order as Picked Up. The order will be marked as Completed once the buyer confirms, or automatically after 3 days if the buyer does not respond.'}
                  </p>
                </div>
              )}

              {orderData.currentStatus === 'OnHold' && lastStatus.name === 'OnHold' && lastStatus.data.type === 'cancellation_request' && (
                <div className="orderDetailSection">
                  <h3>Cancellation Request</h3>
                  {lastStatus.data.requestedBy === role ? (
                    <>
                      <p>You have requested to cancel the order. Waiting for approval from the {role === 'buyer' ? 'seller' : 'buyer'}.</p>
                      {isNonRefundable && !isSeller && (
                        <p style={{ marginTop: '8px', padding: '10px', background: '#fff3cd', borderRadius: '6px', color: '#856404', fontSize: '14px' }}>
                          This order is non-refundable. If the cancellation is accepted, the deposit amount will not be refunded to you.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p>{lastStatus.data.requestedBy.charAt(0).toUpperCase() + lastStatus.data.requestedBy.slice(1)} has requested to cancel the order. Reason: {lastStatus.data.reason}</p>
                      {isNonRefundable && (
                        <p style={{ marginTop: '8px', padding: '10px', background: '#fff3cd', borderRadius: '6px', color: '#856404', fontSize: '14px' }}>
                          This is a non-refundable deposit order. If you accept, the buyer will not receive any refund.
                        </p>
                      )}
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                        <button className='modalButton submit' onClick={handleAcceptCancellation} disabled={isActionLoading}>
                          {isActionLoading ? <Loader type="miniPlus" /> : 'Accept'}
                        </button>
                        <button className='modalButton cancel' onClick={handleRejectCancellation} disabled={isActionLoading}>
                          {isActionLoading ? <Loader type="miniPlus" /> : 'Reject'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {timeLeft !== null && (
                <div className="orderDetailCountdown">
                  <h3>{isSeller ? 'Time to Deliver' : 'Time to Pickup'}</h3>
                  <p className={timeLeft <= 0 ? 'pastDue' : ''}>
                    {formatTimeLeft(timeLeft)} {timeLeft > 0 && `(${isSeller ? orderData.timeToDeliver : orderData.timeframeToCollect})`}
                  </p>
                </div>
              )}

              <div className="orderDetailSection">
                <h3>Order Details</h3>
                <dl className="orderDetailList">
                  <dt>Order Number</dt>
                  <dd>{orderData.customOrderId || 'N/A'}</dd>
                  <dt>Date Placed</dt>
                  <dd>{new Date(orderData.createdAt).toLocaleDateString() || 'N/A'}</dd>
                  <dt>{isSeller ? (orderData.deliveryType === 'delivery' ? 'Time to Deliver' : 'Time to Pickup') : (orderData.deliveryType === 'delivery' ? 'Expected Delivery' : 'Time to Pickup')}</dt>
                  <dd>{orderData.deliveryType === 'delivery' ? orderData.timeToDeliver : orderData.timeframeToCollect || 'N/A'}</dd>
                  {orderData.requestedDeliveryDate && (
                    <>
                      <dt>{orderData.deliveryType === 'delivery' ? 'Requested Delivery Date' : 'Requested Pickup Date'}</dt>
                      <dd className="orderDetailRequestedDate">
                        {new Date(orderData.requestedDeliveryDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </dd>
                    </>
                  )}
                  {orderData.orderType === 'deposit' && (
                    <>
                      <dt>Order Type</dt>
                      <dd>Deposit-Based</dd>
                      {orderData.payment.remainingPayment.status !== 'succeeded' && <><dt>Initial Deposit</dt>
                        {!isSeller && <dd>{formatCurrency(orderData.payment.initialPayment.amount, currency, exchangeRate)} ({orderData.depositPercentage}%)</dd>}
                        {isSeller && <dd>{orderData.depositPercentage}%</dd>}
                        {!isSeller && <><dt>Remaining Balance</dt>
                          <dd>{formatCurrency(orderData.summary.remainingAmount, currency, exchangeRate)}</dd></>}
                        <dt>Remaining Balance Due Date</dt>
                        <dd>{new Date(orderData.remainingBalanceDueDate).toLocaleDateString()}</dd></>}
                    </>
                  )}
                  {orderData?.productInfo?.listingType === 'service' && (
                    <>
                      <dt>Service Mode</dt>
                      <dd style={{ textTransform: 'capitalize' }}>
                        {orderData?.productInfo?.productId?.serviceMode
                          ? orderData.productInfo.productId.serviceMode.replace(/_/g, ' ')
                          : 'N/A'}
                      </dd>
                    </>
                  )}
                </dl>
                {isSeller && orderData.orderType === 'deposit' && orderData.payment.remainingPayment.status !== 'succeeded' && (
                  <p style={{ color: 'red', marginTop: '10px' }}>
                    Note: You can only mark this order as {orderData.deliveryType === 'delivery' ? 'Delivered' : 'Picked Up'} once the buyer pays the remaining balance.
                  </p>
                )}
              </div>
              <div className="orderDetailSellerCard">
                <div className='upper'>
                  {otherPartyInfo.avatar && (
                    <div className="orderDetailSellerAvatar">
                      <img src={otherPartyInfo.avatar} alt={otherPartyInfo.name} />
                    </div>
                  )}
                  <div className="orderDetailSellerInfo">
                    <h3 className="orderDetailSellerName">{otherPartyInfo.name}</h3>
                    {otherPartyInfo.email && (
                      <p className="orderDetailSellerContact">{otherPartyInfo.email}</p>
                    )}
                    {otherPartyInfo.phone && (
                      <p className="orderDetailSellerContact">{otherPartyInfo.phone}</p>
                    )}
                  </div>
                </div>
                <button className='modalButton cancel' onClick={() => {
                  const otherUserId = isSeller ? orderData.buyerId?._id : (orderData.sellerId?.userId?._id || orderData.sellerId?.userId);
                  if (otherUserId) openFloatingChat(otherUserId);
                }}>Contact</button>
              </div>
              {/* <div className="orderDetailPaymentMethod">
                <button className="primaryBtn3">Payment Method: {orderData.payment?.method || 'N/A'}</button>
              </div> */}
            </div>

            <div className="orderDetailRightColumn">
              {orderData.deliveryType === 'pickup' && !(orderData?.productInfo?.listingType === 'service' && orderData?.productInfo?.productId?.serviceMode === 'online') && (
                <div className="orderDetailSection">
                  <h3>Pickup Address</h3>
                  <p>{orderData?.productInfo?.productId?.pickupLocation ? formatFullAddress({
                    address: orderData.productInfo.productId.pickupLocation.address,
                    city: orderData.productInfo.productId.pickupLocation.city,
                    state: orderData.productInfo.productId.pickupLocation.state,
                    country: orderData.productInfo.productId.pickupLocation.country,
                    zipCode: orderData.productInfo.productId.pickupLocation.zipCode,
                  }) : 'N/A'}</p>
                </div>
              )}

              {!isSeller && orderData.deliveryType === 'delivery' && orderData.sellerId?.sellerAddress && (
                <div className="orderDetailSection">
                  <h3>Seller Address</h3>
                  <p>{formatFullAddress(orderData.sellerId.sellerAddress) || 'N/A'}</p>
                </div>
              )}

              <div className="orderDetailSection">
                <h3>{orderData?.productInfo?.listingType === 'service'
                  ? (orderData?.productInfo?.productId?.serviceMode === 'onsite' ? 'Service Location' : 'Buyer Address')
                  : orderData.deliveryType === 'delivery' ? 'Delivery Address' : 'Buyer Address'}</h3>
                <div>
                  <p>{orderData.deliveryAddress ? formatFullAddress({
                    address: orderData.deliveryAddress.unitNumber
                      ? `${orderData.deliveryAddress.address}, ${orderData.deliveryAddress.unitNumber}`
                      : orderData.deliveryAddress.address,
                    city: orderData.deliveryAddress.city,
                    state: orderData.deliveryAddress.state,
                    country: orderData.deliveryAddress.country,
                    zipCode: orderData.deliveryAddress.postalCode || orderData.deliveryAddress.zipCode,
                  }) : 'N/A'}</p>
                  {orderData.deliveryAddress?.note && (
                    <p style={{ marginTop: '7px' }}>
                      <span style={{ fontWeight: 500, color: '#333' }}>Note from Buyer:</span> {orderData.deliveryAddress.note}
                    </p>
                  )}
                </div>
              </div>

              <div className="orderDetailSection">
                <h3>{orderData?.productInfo?.listingType === 'service' ? 'Service Details' : 'Product Details'}</h3>
                <table className="orderDetailTable">
                  <tbody>
                    <tr>
                      <td>{orderData?.productInfo?.listingType === 'service' ? 'Service' : 'Product'}</td>
                      <td>{orderData.productInfo?.title || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td>Options</td>
                      <td>{getSelectedOptionsDescription(orderData)}</td>
                    </tr>
                    <tr>
                      <td>Quantity</td>
                      <td>{orderData.summary?.quantity || 'N/A'}</td>
                    </tr>
                    <tr>
                      <td>Price per Unit</td>
                      <td>{formatCurrency(orderData.summary?.price, currency, exchangeRate) || '0.00'}</td>
                    </tr>
                    <tr>
                      <td>Subtotal</td>
                      <td>{formatCurrency(orderData.summary?.subtotal, currency, exchangeRate) || '0.00'}</td>
                    </tr>
                    {orderData.addedServicesTotal > 0 && (
                      <tr>
                        <td>Added Services</td>
                        <td>+{formatCurrency(orderData.addedServicesTotal, currency, exchangeRate)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {orderData.addedServices?.length > 0 && (
                  <div className="orderAddedServices">
                    <p className="orderAddedServicesLabel">Added Services:</p>
                    {orderData.addedServices.map((svc, i) => (
                      <div key={i} className="orderAddedServiceItem">
                        {svc.image && <img src={svc.image} alt={svc.name} className="orderAddedServiceImg" />}
                        <span className="orderAddedServiceName">{svc.name}</span>
                        <span className="orderAddedServicePrice">{parseFloat(svc.price) > 0 ? formatCurrency(svc.price, currency, exchangeRate) : 'Free'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {orderData?.buyerRequirements?.length > 0 && (
                <div className="orderDetailSection">
                  <h3>Buyer Requirements</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {orderData.buyerRequirements.map((req, idx) => (
                      <div key={idx} style={{ padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                        <p style={{ fontWeight: 600, marginBottom: '4px' }}>Q: {req.question}</p>
                        <p style={{ color: '#555' }}>A: {req.answer || 'Not answered'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="orderDetailSection">
                <h3>{isSeller ? 'Earnings Details' : 'Billing Details'}</h3>
                <table className="orderDetailTable">
                  <tbody>
                    <tr>
                      <td>Subtotal</td>
                      <td>{formatCurrencyDual(billingDetails.subtotal, currency, exchangeRate)}</td>
                    </tr>
                    <tr>
                      <td>{orderData?.productInfo?.listingType === 'service' ? 'Travel / Delivery Fee' : 'Delivery Fee'}</td>
                      <td>{formatCurrencyDual(billingDetails.deliveryFee, currency, exchangeRate)}</td>
                    </tr>
                    <tr>
                      <td>Service Fee</td>
                      <td>{formatCurrencyDual(billingDetails.serviceFee, currency, exchangeRate)}</td>
                    </tr>
                    {!isSeller && (
                      <tr>
                        <td>Tax</td>
                        <td>{formatCurrencyDual(billingDetails.tax, currency, exchangeRate)}</td>
                      </tr>
                    )}
                    <tr className="orderDetailTotalRow">
                      <td><strong>{isSeller ? "Amount you'll receive" : 'Total'}</strong></td>
                      <td><strong>{formatCurrencyDual(billingDetails.total, currency, exchangeRate)}</strong></td>
                    </tr>
                    {!isSeller && orderData.orderType === 'deposit' && <tr className="orderDetailTotalRow">
                      <td><strong>Amount Paid</strong></td>
                      <td><strong>{formatCurrencyDual(orderData?.summary.depositAmount, currency, exchangeRate)}</strong></td>
                    </tr>}
                    {!isSeller && orderData.orderType === 'deposit' && <tr className="orderDetailTotalRow">
                      <td><strong>Remaining Amount</strong></td>
                      <td><strong>{formatCurrencyDual(orderData?.summary.remainingAmount, currency, exchangeRate)}</strong></td>
                    </tr>}
                  </tbody>
                </table>
                {isSeller && <PayoutFeeNote />}
              </div>

              <div className="orderDetailDocumentsSection">
                <h3>Documents</h3>
                <div className="orderDetailDocumentButtons">
                  <button type="button" className="orderDetailDocBtn" onClick={handleGenerateInvoice} title="Generate Invoice">
                    <HiDocumentText size={18} /> Invoice
                  </button>
                  {isSeller && (
                    <button type="button" className="orderDetailDocBtn" onClick={handleGeneratePackingSlip} title="Generate Packing Slip">
                      <HiDocumentText size={18} /> Packing Slip
                    </button>
                  )}
                </div>
              </div>

              <div className="orderDetailActionButtons">
                <button
                  className="orderDetailCancelButton"
                  onClick={handleCancelOrder}
                  disabled={(orderData.currentStatus !== 'Active' && orderData.timelineStatus !== 'In Progress' && orderData.timelineStatus !== 'Preparing Order') || (!isSeller && orderData?.productInfo?.productId?.cancellable === "no")}
                >
                  Cancel Order
                </button>
                <button
                  className="orderDetailDisputeButton"
                  onClick={() => setShowStartDisputeModal(true)}
                  disabled={orderData.currentStatus === 'Completed' || orderData.currentStatus === 'Cancelled' || orderData.currentStatus === 'Resolved' || orderData.dispute}
                >
                  {orderData.dispute ? 'Dispute Open' : 'Start Dispute'}
                </button>
              </div>

              {!isSeller && orderData?.productInfo?.productId?.cancellable === "no" && <div className="infoText">
                <FiInfo className="infoIcon2" /> <span>You aren't allowed to cancel this Order. Contact Seller if you want to.</span>
              </div>}
            </div>
          </div>

          {/* Order History (status + order chat messages) */}
          <div className="orderHistorySection">
            <h3>Order History</h3>
            <div className="orderHistoryTimeline">
              {(() => {
                const statusEntries = (orderData.statusHistory && orderData.statusHistory.length > 0)
                  ? [...orderData.statusHistory].reverse().map((entry, idx) => ({
                    type: 'status',
                    key: `status-${idx}`,
                    ts: new Date(entry.createdAt).getTime(),
                    entry
                  }))
                  : [{ type: 'status', key: 'status-0', ts: new Date(orderData.createdAt).getTime(), entry: { name: 'Order Placed', createdAt: orderData.createdAt, data: null } }];

                const chatEntries = orderChatMessages.filter(msg => msg.kind !== 'system').map((msg, idx) => ({
                  type: 'chat',
                  key: `chat-${msg._id || idx}`,
                  ts: new Date(msg.timestamp).getTime(),
                  msg
                }));

                const combined = [...statusEntries, ...chatEntries].sort((a, b) => a.ts - b.ts);
                const otherPartyName = isSeller
                  ? (orderData.buyerInfo?.fullName || 'Buyer')
                  : (orderData.sellerId?.displayName || orderData.sellerId?.companyName || 'Seller');

                return combined.map((item) => {
                  if (item.type === 'status') {
                    const { entry } = item;
                    return (
                      <div key={item.key} className="orderHistoryItem">
                        <div className="orderHistoryItemName">{entry.name}</div>
                        <div className="orderHistoryItemDate">{new Date(entry.createdAt).toLocaleString()}</div>
                        {entry.data && Object.keys(entry.data).length > 0 && (
                          <div className="orderHistoryItemData">
                            {entry.data.trackingId && <><strong>Tracking ID:</strong> {entry.data.trackingId}<br /></>}
                            {entry.data.deliveryPartner && <><strong>Delivery Partner:</strong> {entry.data.deliveryPartner}<br /></>}
                            {entry.data.trackingLink && <a href={entry.data.trackingLink} target="_blank" rel="noopener noreferrer">Track shipment</a>}
                            {entry.data.type === 'cancellation_request' && (
                              <><strong>Requested by:</strong> {entry.data.requestedBy}<br />
                                {entry.data.reason && <><strong>Reason:</strong> {entry.data.reason}</>}</>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  const { msg } = item;
                  const msgId = msg._id?.toString() || item.key;
                  const isSentByMe = (msg.senderId?._id || msg.senderId)?.toString() === user?._id?.toString();
                  const isSystem = msg.kind === 'system';
                  const label = isSystem
                    ? (msg.text || 'Order update')
                    : (isSentByMe ? 'You sent a message' : `${otherPartyName} sent a message`);
                  const isCollapsed = collapsedChatIds[msgId] === true;

                  return (
                    <div key={item.key} className="orderHistoryItem orderHistoryChatItem">
                      <button
                        type="button"
                        className="orderHistoryChatToggle"
                        onClick={() => toggleChatExpanded(msgId)}
                      >
                        <span className="orderHistoryChatLabel">{label}</span>
                        <span className="orderHistoryChatDate">{formatDateTime(msg.timestamp)}</span>
                        {isCollapsed ? <FaChevronDown size={14} /> : <FaChevronUp size={14} />}
                      </button>
                      {!isCollapsed && (
                        <div className="orderHistoryChatContent">
                          {msg.text && <p className="orderHistoryChatText">{msg.text}</p>}
                          {msg.fileUrl && (
                            <div className="orderHistoryChatAttachments">
                              {msg.fileType?.startsWith('image/') && (
                                <img
                                  src={msg.fileUrl}
                                  alt="Attachment"
                                  className="orderHistoryChatImage"
                                  onClick={(e) => { e.stopPropagation(); setPreviewImage(msg.fileUrl); }}
                                />
                              )}
                              {(msg.fileType?.startsWith('application/') || msg.fileType?.startsWith('text/')) && (
                                <div className="orderHistoryChatDoc" onClick={(e) => { e.stopPropagation(); handleFileDownload(msg.fileUrl, msg.originalFileName); }}>
                                  {renderDocumentIcon(msg.fileType)}
                                  <span>{msg.originalFileName || 'File'}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Order Chat Input (bottom bar only) */}
          <OrderChatInput orderId={orderData._id} onMessageSent={(msg) => setOrderChatMessages(prev => [...prev, msg])} />
        </div>

        {orderData.postedReview && (
          <div className="orderDetailSection">
            <h3>Review</h3>
            <ReviewHistoryContainer
              reviews={[orderData.postedReview]}
              isMyReview={!isSeller}
              isProductReview={true}
            />
          </div>
        )}
      </section>

      {showReviewPopup && selectedReview && (
        <ReviewPopup
          review={selectedReview}
          onClose={() => setShowReviewPopup(false)}
          onSubmit={handleSubmitReview}
        />
      )}

      {cancelModalOpen && (
        <div className="modalOverlay" onClick={() => setCancelModalOpen(false)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Order</h3>
            <p>Cancelling the order will send a request to the {isSeller ? 'buyer' : 'seller'}, and the order will be cancelled once approved.</p>
            {isNonRefundable && (
              <p style={{ marginTop: '8px', padding: '10px', background: '#f8d7da', borderRadius: '6px', color: '#721c24', fontSize: '14px' }}>
                This is a non-refundable deposit order. If the cancellation is accepted, the buyer will not receive any refund for the amount paid.
              </p>
            )}
            <textarea
              placeholder="Please provide a reason for cancellation"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className='inputField'
            />
            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', justifyContent: "center" }}>
              <button className='modalButton cancel' onClick={() => setCancelModalOpen(false)}>Close</button>
              <button className='modalButton submit' onClick={submitCancelRequest} disabled={isActionLoading || !cancelReason.trim()}>
                {isActionLoading ? <Loader type="miniPlus" /> : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal.open && (
        <div className="modalOverlay" onClick={() => setModal({ ...modal, open: false })}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()}>
            <p>{modal.message}</p>
            {modal.type === 'message' ? (
              <button onClick={() => setModal({ ...modal, open: false })} className='modalButton submit'>OK</button>
            ) : (
              <div style={{ display: 'flex', gap: '10px', justifyContent: "center" }}>
                <button onClick={() => { modal.onConfirm(); setModal({ ...modal, open: false }); }} className='modalButton submit'>Yes</button>
                <button onClick={() => setModal({ ...modal, open: false })} className='modalButton cancel'>No</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showReviewPostedPopup && (
        <div className="modalOverlay">
          <div className="modalContent" style={{ textAlign: "center" }}>
            <p>The review has been submitted. Thank you for your review!</p>
            <button onClick={() => { setShowReviewPostedPopup(false); localStorage.removeItem('Review Submitted'); }} className="modalButton cancel" style={{ marginTop: "10px 0px" }}>Close</button>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="modalOverlay" onClick={() => setPreviewImage(null)}>
          <div className="modalContent" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '780px', width: '95%' }}>
            <h3 style={{ marginBottom: '12px' }}>Image Preview</h3>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={previewImage} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '10px' }} />
            </div>
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <button onClick={() => setPreviewImage(null)} className="modalButton cancel">Close</button>
            </div>
          </div>
        </div>
      )}

      {showStartDisputeModal && (
        <StartDisputeModal
          isOpen={showStartDisputeModal}
          onClose={() => setShowStartDisputeModal(false)}
          orderId={orderData._id}
          listingType={orderData?.productInfo?.listingType}
          onSuccess={refreshOrder}
        />
      )}

      {showPaymentOverlay && (
        <PaymentOverlay
          isOpen={showPaymentOverlay}
          onClose={() => setShowPaymentOverlay(false)}
          showTotalOnly={true}
          paymentDetails={{
            price: orderData.summary.remainingAmount,
            tax: 0,
            total: orderData.summary.remainingAmount,
          }}
          createPaymentIntent={async () => {
            const token = localStorage.getItem('token');
            const response = await axios.post(
              `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/create-remaining-payment-intent`,
              { orderId: orderData._id },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            return response.data.clientSecret;
          }}
          onPaymentSuccess={async (paymentIntent) => {
            try {
              const token = localStorage.getItem('token');
              await axios.post(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/confirm-remaining-payment`,
                { orderId: orderData._id, paymentIntentId: paymentIntent.id },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              setModal({
                open: true,
                type: 'message',
                message: 'Remaining payment successful. Please refresh the page to see the updated status.',
                onConfirm: null,
              });
              const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/orders/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setOrderData(response.data.order);
            } catch (error) {
              console.error('Error confirming remaining payment:', error);
              setModal({
                open: true,
                type: 'message',
                message: 'An error occurred while confirming the payment',
                onConfirm: null,
              });
            }
          }}
          title="Pay Remaining Balance"
        />
      )}
    </div>
  );
};

export default OrderDetailsPage;